import { useState, useEffect } from 'react';

/**
 * Хук useLocalStorage - сохранение и получение данных из localStorage
 * 
 * @param {string} key - Ключ для хранения в localStorage
 * @param {any} initialValue - Начальное значение, если в localStorage ничего нет
 * @returns {Array} [storedValue, setValue] - Текущее значение и функция для его изменения
 */
const useLocalStorage = (key, initialValue) => {
  // Получаем значение из localStorage или используем initialValue
  const getStoredValue = () => {
    try {
      const item = window.localStorage.getItem(key);
      // Если значение существует, парсим JSON, иначе возвращаем initialValue
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  };
  
  // Устанавливаем начальное состояние
  const [storedValue, setStoredValue] = useState(getStoredValue);
  
  // Функция для обновления localStorage и состояния
  const setValue = (value) => {
    try {
      // Позволяем value быть функцией, как в setState
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      
      // Обновляем состояние React
      setStoredValue(valueToStore);
      
      // Обновляем localStorage
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
    }
  };
  
  // Эффект для синхронизации с другими вкладками/окнами
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === key) {
        try {
          const newValue = event.newValue ? JSON.parse(event.newValue) : initialValue;
          setStoredValue(newValue);
        } catch (error) {
          console.error(`Error parsing localStorage change for key "${key}":`, error);
        }
      }
    };
    
    // Добавляем слушатель событий для отслеживания изменений в localStorage
    window.addEventListener('storage', handleStorageChange);
    
    // Очистка слушателя при размонтировании компонента
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [key, initialValue]);
  
  return [storedValue, setValue];
};

export default useLocalStorage;